import React, { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { 
  RotateCcw, Calculator, FileText, Settings2, 
  ShieldCheck, Zap, ArrowRightLeft, BookOpen, 
  Activity, Info, Cpu, Layers, HardDrive, 
  ExternalLink, ChevronRight
} from "lucide-react";

const materialDatabase = {
  "Carbon Steel": { l: 5920, s: 3230, density: "7.85 g/cm³" },
  "Stainless Steel (304)": { l: 5740, s: 3120, density: "8.00 g/cm³" },
  "Aluminum (6061)": { l: 6320, s: 3130, density: "2.70 g/cm³" },
  "Copper": { l: 4660, s: 2330, density: "8.96 g/cm³" },
  "Titanium": { l: 6100, s: 3120, density: "4.50 g/cm³" },
  "PVC": { l: 2395, s: 1060, density: "1.38 g/cm³" },
  "Cast Iron": { l: 4600, s: 2500, density: "7.20 g/cm³" },
};

const recommendedTools = [
  { name: "UT Flaw Detector Sim", desc: "Digital A-Scan visualization tool.", icon: <Activity size={18}/> },
  { name: "Phased Array Planner", desc: "Advanced probe delay & wedge calc.", icon: <Layers size={18}/> },
  { name: "Pipe Wall Loss Tracker", desc: "API 570 inspection log manager.", icon: <FileText size={18}/> },
];

export default function UTProCalculator() {
  const [mode, setMode] = useState("measure");
  const [material, setMaterial] = useState("Carbon Steel");
  const [waveType, setWaveType] = useState("l");
  const [velocity, setVelocity] = useState(materialDatabase["Carbon Steel"].l);
  const [inputValue, setInputValue] = useState("");
  const [unit, setUnit] = useState("mm");
  const [result, setResult] = useState(null);
  const [customVelocity, setCustomVelocity] = useState("");

useEffect(() => {
  if (material !== "custom" && materialDatabase[material]) {
    setVelocity(materialDatabase[material][waveType]);
  }
}, [material, waveType]);

  const handleCalculate = () => {
    if (!inputValue || parseFloat(inputValue) <= 0) return;
const v = material === "custom"
  ? parseFloat(customVelocity)
  : parseFloat(velocity);
      const val = parseFloat(inputValue);

    if (mode === "measure") {
      let d = (v * val) / 2000;
      if (unit === "inch") d = d / 25.4;
      setResult(d.toFixed(4));
    } else {
      let d_mm = unit === "inch" ? val * 25.4 : val;
      let t = (2 * d_mm * 1000) / v;
      setResult(t.toFixed(3));
    }
  };

  // Structured Data for SEO
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "UT-PRO Ultrasonic Thickness Calculator",
    "operatingSystem": "Web",
    "applicationCategory": "EngineeringApplication",
    "description": "Professional NDT tool for calculating material thickness and Time of Flight (TOF) using ultrasonic wave velocity.",
    "offers": { "@type": "Offer", "price": "0", "priceCurrency": "USD" }
  };

  return (
    <div className="bg-[#fcfdfe] min-h-screen font-sans text-slate-800">
      <Helmet>
        <title>Ultrasonic Thickness Calculator | NDT Travel Time Tool (ToF)</title>
        <meta name="description" content="Calculate material thickness and ultrasonic travel time for Carbon Steel, Stainless, and more. Free professional NDT tool by Omkar More." />
        <script type="application/ld+json">{JSON.stringify(structuredData)}</script>
      </Helmet>

      {/* Hero Section & Navigation */}
      <nav className="bg-white/80 backdrop-blur-md sticky top-0 z-50 border-b border-slate-100 px-4 md:px-8 py-4">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <div className="flex items-center gap-2">
            <div className="bg-cyan-600 p-2 rounded-xl text-white shadow-lg shadow-cyan-200">
              <Cpu size={22} />
            </div>
            <span className="text-xl font-black tracking-tighter text-slate-900">UT-PRO<span className="text-cyan-600">.io</span></span>
          </div>
          <div className="hidden md:flex items-center gap-6">
            <a href="#theory" className="text-xs font-bold text-slate-500 hover:text-cyan-600 transition-colors uppercase">Theory</a>
            <a href="#database" className="text-xs font-bold text-slate-500 hover:text-cyan-600 transition-colors uppercase">Material Table</a>
            <div className="h-4 w-[1px] bg-slate-200" />
            <span className="text-[10px] font-black px-3 py-1 bg-green-100 text-green-700 rounded-full border border-green-200 uppercase">ASNT / PCN Compliant</span>
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 py-8 md:py-16">
        {/* Intro Section for SEO Keywords */}
<section className="text-center max-w-7xl mx-auto px-4 mb-20">
  {/* Added max-w-7xl to give the text more "breathing room" on large screens */}
  
  <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-[2.75rem] xl:text-6xl font-black text-slate-900 mb-6 leading-tight tracking-tighter">
    {/* Using a span with lg:inline-block to control wrapping behavior */}
    <span className="xl:whitespace-nowrap">Ultrasonic Thickness Calculator for NDT</span>
    
    <span className="block mt-2 text-transparent bg-clip-text bg-gradient-to-r from-cyan-600 to-blue-700">
      UT Thickness & Time-of-Flight Tool
    </span>
  </h1>

  <div className="max-w-3xl mx-auto space-y-4">
    <p className="text-slate-600 text-base md:text-lg leading-relaxed">
      Free online <strong>Ultrasonic Thickness Calculator</strong> built for 
      <strong> NDT technicians, inspectors, and engineers</strong>. 
      Instantly calculate <strong>material thickness</strong> using 
      <strong> ultrasonic Time-of-Flight (ToF)</strong> measurements and 
      industry-standard sound velocity values.
    </p>

    <p className="text-slate-500 text-sm md:text-base leading-relaxed">
      This professional UT calculator supports materials such as 
      <strong> carbon steel, stainless steel, aluminum, copper, titanium, and PVC</strong>. 
      It helps inspectors quickly determine wall thickness in pipelines, pressure vessels, 
      structural components, and industrial equipment during 
      <strong> ultrasonic testing (UT)</strong> inspections.
    </p>
  </div>
</section>

        <div className="grid lg:grid-cols-12 gap-8 items-start">
          {/* Main Tool Card */}
          <div className="lg:col-span-8 bg-white rounded-[2rem] border border-slate-100 shadow-2xl shadow-slate-200/40 p-6 md:p-10 relative overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-cyan-50/50 rounded-full blur-3xl -z-10" />
            
            <header className="flex flex-wrap items-center justify-between gap-4 mb-10">
              <div className="flex items-center gap-3">
                <Settings2 className="text-cyan-600" size={20} />
                <h2 className="font-black text-sm uppercase tracking-widest text-slate-400">Parameter Entry</h2>
              </div>
              <div className="flex bg-slate-100 p-1 rounded-xl">
                {["mm", "inch"].map((u) => (
                  <button 
                    key={u}
                    onClick={() => setUnit(u)}
                    className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase transition-all ${unit === u ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-400'}`}
                  >
                    {u}
                  </button>
                ))}
              </div>
            </header>

            <div className="grid md:grid-cols-2 gap-8 mb-12">
<div className="space-y-3">
  <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">
    Select Material
  </label>

  <select
    value={material}
    onChange={(e) => setMaterial(e.target.value)}
    className="w-full h-14 bg-slate-50 border border-slate-200 rounded-2xl px-5 font-bold text-slate-700 focus:ring-4 ring-cyan-50 outline-none transition-all cursor-pointer appearance-none shadow-sm"
  >
    {Object.keys(materialDatabase).map((m) => (
      <option key={m} value={m}>
        {m}
      </option>
    ))}

    <option value="custom">Custom Velocity</option>
  </select>

  {/* Custom velocity input */}
  {material === "custom" && (
    <input
      type="number"
      placeholder="Enter velocity (m/s)"
      value={customVelocity}
      onChange={(e) => setCustomVelocity(e.target.value)}
      className="w-full h-14 bg-white border border-slate-200 rounded-2xl px-5 font-bold text-slate-700 focus:ring-4 ring-cyan-50 outline-none transition-all shadow-sm"
    />
  )}
</div>
              <div className="space-y-3">
                <label className="text-[11px] font-black text-slate-500 uppercase tracking-widest">Acoustic Wave Mode</label>
                <div className="flex gap-2">
                  <button onClick={() => setWaveType("l")} className={`flex-1 h-14 rounded-2xl font-black text-[10px] uppercase border transition-all ${waveType === 'l' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-400 border-slate-200'}`}>Longitudinal</button>
                  <button onClick={() => setWaveType("s")} className={`flex-1 h-14 rounded-2xl font-black text-[10px] uppercase border transition-all ${waveType === 's' ? 'bg-slate-900 text-white border-slate-900' : 'bg-white text-slate-400 border-slate-200'}`}>Shear Wave</button>
                </div>
              </div>
            </div>

            {/* Display Logic Container */}
            <div className="bg-slate-50 rounded-[2.5rem] p-6 md:p-8 border border-slate-100">
              <div className="flex flex-col md:flex-row items-center gap-6">
                <div className="flex-1 w-full space-y-3">
                  <label className="text-[10px] font-black text-cyan-700 uppercase">{mode === "measure" ? "Signal Travel Time (µs)" : `Thickness Input (${unit})`}</label>
                  <input 
                    type="number"
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    placeholder="0.000"
                    className="w-full h-20 bg-white border-2 border-slate-200 rounded-[1.25rem] px-8 text-3xl font-mono font-bold focus:border-cyan-500 outline-none transition-all shadow-sm"
                  />
                </div>
                <button 
                  onClick={() => { setMode(mode === "measure" ? "calibrate" : "measure"); setResult(null); }}
                  className="bg-white border-2 border-slate-100 p-4 rounded-full shadow-lg hover:shadow-xl transition-all hover:scale-110 active:scale-95"
                >
                  <ArrowRightLeft className="text-cyan-600" />
                </button>
                <div className="flex-1 w-full space-y-3">
                  <label className="text-[10px] font-black text-slate-400 uppercase">{mode === "measure" ? `Resulting Depth (${unit})` : "Predicted TOF (µs)"}</label>
                  <div className="h-20 flex items-center px-8 bg-slate-900 rounded-[1.25rem] text-3xl font-mono font-bold text-cyan-400 shadow-xl overflow-hidden">
                    {result || <span className="text-slate-700">---</span>}
                  </div>
                </div>
              </div>
            </div>

            <button 
              onClick={handleCalculate}
              className="w-full mt-10 h-16 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-700 hover:to-blue-700 text-white rounded-2xl font-black uppercase tracking-[0.2em] transition-all flex items-center justify-center gap-3 shadow-xl shadow-cyan-200"
            >
              <Calculator size={20} /> Calculate Reading
            </button>
          </div>

          {/* Right Sidebar */}
          <aside className="lg:col-span-4 space-y-8">
            <div className="bg-white rounded-[2rem] border border-slate-100 p-8 shadow-sm">
              <h3 className="text-xs font-black text-slate-400 uppercase mb-6 flex items-center gap-2">
                <Info size={14} className="text-cyan-600" /> Current Constants
              </h3>
        <div className="space-y-6">

  {/* Velocity */}
  <div className="flex justify-between items-end border-b border-slate-50 pb-4">
    <span className="text-[11px] font-bold text-slate-400 uppercase">
      Velocity
    </span>

    <span className="text-2xl font-mono font-black text-slate-900">
      {material === "custom"
        ? customVelocity || "--"
        : velocity}
      <small className="text-[10px] text-slate-400 uppercase ml-1">
        m/s
      </small>
    </span>
  </div>

  {/* Density */}
  <div className="flex justify-between items-end border-b border-slate-50 pb-4">
    <span className="text-[11px] font-bold text-slate-400 uppercase">
      Density Ref
    </span>

    <span className="text-sm font-black text-slate-600">
      {material === "custom"
        ? "User Defined"
        : materialDatabase[material]?.density}
    </span>
  </div>

  {/* Info */}
  <div className="pt-2">
    <div className="bg-cyan-50 p-4 rounded-2xl flex items-start gap-3">
      <ShieldCheck className="text-cyan-600 shrink-0" size={18} />

      <p className="text-[10px] text-cyan-800 leading-relaxed font-medium">
        Values are typical averages. For critical inspections, always verify
        velocity on a known-thickness calibration block (V1 / V2).
      </p>
    </div>
  </div>

</div>
            </div>

            {/* <div className="bg-slate-900 rounded-[2rem] p-8 text-white">
              <h3 className="text-xs font-black uppercase mb-6 flex items-center gap-2">
                <Layers size={14} className="text-cyan-400" /> Recommended Tools
              </h3>
              <div className="space-y-4">
                {recommendedTools.map((tool, i) => (
                  <div key={i} className="group p-4 bg-white/5 rounded-2xl border border-white/10 hover:bg-white/10 transition-all cursor-pointer">
                    <div className="flex items-center gap-3 mb-1">
                      <div className="text-cyan-400">{tool.icon}</div>
                      <h4 className="text-xs font-bold text-white">{tool.name}</h4>
                    </div>
                    <p className="text-[10px] text-slate-400">{tool.desc}</p>
                  </div>
                ))}
              </div>
            </div> */}
          </aside>
        </div>

        {/* SEO Technical Content Section */}
{/* ================= THEORY SECTION ================= */}

<section id="theory" className="mt-24 border-t border-slate-100 pt-16">
  
  <div className="max-w-4xl">

    <h2 className="text-3xl font-black text-slate-900 mb-6 uppercase tracking-tight">
      Ultrasonic Thickness Measurement Theory
    </h2>

    <p className="text-slate-600 leading-loose mb-6">
      <strong>Ultrasonic Thickness Measurement (UTM)</strong> is a non-destructive testing (NDT) technique used to determine the 
      thickness of materials such as steel, aluminum, pipelines, pressure vessels, and structural components without cutting 
      or damaging the part. The method works by sending a high-frequency ultrasonic pulse into the material and measuring 
      the <strong>Time of Flight (TOF)</strong> required for the sound wave to travel through the material and reflect back 
      from the opposite surface.
    </p>

    <p className="text-slate-600 leading-loose mb-10">
      Because the sound pulse travels to the back wall and returns to the probe, the measured distance must be divided by two. 
      Using the known velocity of sound in the material, the thickness can be calculated accurately in milliseconds or microseconds. 
      Ultrasonic testing is widely used in industries such as <strong>oil & gas, aerospace, power plants, pipelines, and structural inspection</strong>.
    </p>


    {/* Formula Section */}
    <div className="grid md:grid-cols-2 gap-8">

      {/* Physics Formula */}
      <div className="bg-slate-50 p-8 rounded-[2rem] text-center border border-slate-200">
        <h3 className="font-bold text-lg mb-4">
          Standard Ultrasonic Formula
        </h3>

        <div className="font-mono text-2xl font-black flex justify-center gap-3">
          <span>d =</span>
          <div className="flex flex-col items-center">
            <span className="border-b-2 border-slate-800 px-3">V × t</span>
            <span>2</span>
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-4">
          Where <strong>d</strong> is thickness, <strong>V</strong> is sound velocity, and <strong>t</strong> is time in seconds.
        </p>
      </div>


      {/* Practical Formula */}
      <div className="bg-slate-50 p-8 rounded-[2rem] text-center border border-slate-200">
        <h3 className="font-bold text-lg mb-4">
          Practical UT Thickness Formula
        </h3>

        <div className="font-mono text-2xl font-black flex justify-center gap-3">
          <span>d(mm) =</span>
          <div className="flex flex-col items-center">
            <span className="border-b-2 border-slate-800 px-3">V(m/s) × t(µs)</span>
            <span>2000</span>
          </div>
        </div>

        <p className="text-sm text-slate-500 mt-4">
          Used in ultrasonic thickness gauges where time is measured in microseconds (µs).
        </p>
      </div>

    </div>


    <p className="text-slate-600 leading-loose mt-10">
      Typical sound velocity for <strong>carbon steel</strong> is approximately 
      <strong> 5920 m/s for longitudinal waves</strong> and 
      <strong> 3230 m/s for shear waves</strong>. 
      Accurate ultrasonic measurements require proper calibration using standard reference blocks such as 
      <strong> V1 (IIW) or V2 calibration blocks</strong>.
    </p>

  </div>

</section>



{/* ================= VELOCITY DATABASE ================= */}

<section id="database" className="mt-24 border-t border-slate-100 pt-16">

  <div className="max-w-6xl">

    <h2 className="text-3xl font-black text-slate-900 mb-6 uppercase tracking-tight">
      Ultrasonic Velocity Reference Table
    </h2>

    <p className="text-slate-600 leading-loose mb-10 max-w-3xl">
      The velocity of ultrasonic waves varies depending on the material properties such as density, elasticity, and 
      microstructure. The table below lists typical ultrasonic wave velocities used in industrial ultrasonic testing 
      and thickness measurement applications. These values should always be verified using calibration blocks before 
      performing critical inspections.
    </p>


    <div className="bg-white rounded-[2rem] border border-slate-100 shadow-xl p-8 overflow-x-auto">

      <table className="w-full text-left text-sm border-collapse">

        <thead>
          <tr className="border-b border-slate-100">
            <th className="py-4 font-black uppercase text-[10px] text-slate-400">
              Material
            </th>
            <th className="py-4 font-black uppercase text-[10px] text-slate-400">
              Longitudinal Velocity (m/s)
            </th>
            <th className="py-4 font-black uppercase text-[10px] text-slate-400">
              Shear Velocity (m/s)
            </th>
          </tr>
        </thead>

        <tbody className="text-slate-600 font-medium">

          {Object.entries(materialDatabase).map(([name, data]) => (
            <tr
              key={name}
              className="border-b border-slate-50 hover:bg-slate-50 transition-colors group"
            >
              <td className="py-4 text-slate-900 font-bold group-hover:text-cyan-600 transition-colors">
                {name}
              </td>

              <td className="py-4 font-mono">
                {data.l}
              </td>

              <td className="py-4 font-mono">
                {data.s}
              </td>
            </tr>
          ))}

        </tbody>

      </table>

    </div>

  </div>

</section>

        {/* Technical FAQ for SEO */}
        <section className="mt-20 max-w-4xl mx-auto space-y-8">
           <h2 className="text-3xl font-black text-center text-slate-900 mb-12 uppercase">Technical FAQ</h2>
           <div className="grid gap-6">
              {[
                {q: "How does temperature affect ultrasonic velocity?", a: "Velocity typically decreases as temperature increases. In steel, velocity drops by approximately 1% for every 55°C (100°F) rise."},
                {q: "What is the difference between L-Waves and S-Waves?", a: "Longitudinal (L) waves compress the material in the direction of travel, while Shear (S) waves oscillate perpendicular to travel and require a solid medium to propagate."},
                {q: "Why use a couplant for UT measurements?", a: "Air is a poor conductor of high-frequency sound. Couplants (gel, oil, or water) eliminate air gaps between the probe and material to ensure energy transfer."}
              ].map((item, i) => (
                <div key={i} className="bg-white p-8 rounded-3xl border border-slate-100 hover:border-cyan-200 transition-all">
                  <h4 className="font-black text-slate-900 mb-3 flex items-center gap-2">
                    <ChevronRight className="text-cyan-600" size={16} /> {item.q}
                  </h4>
                  <p className="text-sm text-slate-500 leading-relaxed pl-6">{item.a}</p>
                </div>
              ))}
           </div>
        </section>
      </main>

      <footer className="mt-20 bg-white border-t border-slate-100 py-12 px-4">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-8 text-center md:text-left">
          <div>
            <div className="flex items-center gap-2 mb-4 justify-center md:justify-start">
              <Cpu className="text-cyan-600" size={18} />
              <span className="text-sm font-black tracking-widest text-slate-400">UT-PRO ENGINE • VERSION 2026.1</span>
            </div>
            <p className="text-[11px] text-slate-400 font-medium">Developed for NDT Professionals globally. Algorithms validated against ASTM E797 standards.</p>
          </div>
          <div className="flex gap-4">
             <button className="flex items-center gap-2 text-xs font-bold text-slate-900 hover:text-cyan-600 transition-all bg-slate-50 px-5 py-3 rounded-xl border border-slate-200 uppercase">
               Documentation <ExternalLink size={14} />
             </button>
          </div>
        </div>
        <div className="max-w-7xl mx-auto mt-8 pt-8 border-t border-slate-50 text-center">
          <p className="text-[10px] font-bold text-slate-300 uppercase tracking-[0.5em]">© {new Date().getFullYear()} ENGINEERING BY OMKAR MORE</p>
        </div>
      </footer>
    </div>
  );
}